import React from "react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";

const EFFECTIVE = "June 2026";
const CONTACT_EMAIL = "info@niijiglobal.com";

function LegalShell({ eyebrow, title, subtitle, children }) {
  return (
    <div>
      <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle} />
      <section className="py-20 bg-ink">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-gold/60 mb-12">
              Effective {EFFECTIVE}
            </p>
            <div className="space-y-12">{children}</div>
            <div className="gold-hairline my-14" />
            <p className="font-sans text-sm text-[#A19D98] leading-relaxed">
              Questions about this document may be directed to{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-gold hover:underline">{CONTACT_EMAIL}</a>.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

const Clause = ({ n, t, children }) => (
  <div>
    <h2 className="font-serif text-2xl sm:text-3xl text-[#F5F0EB] flex items-baseline gap-3">
      <span className="font-mono text-sm text-gold/60">{n}</span> {t}
    </h2>
    <div className="font-sans text-sm text-[#A19D98] leading-relaxed mt-4 space-y-3">{children}</div>
  </div>
);

export function PrivacyPolicy() {
  return (
    <LegalShell
      eyebrow="Legal · Privacy"
      title="Privacy Policy"
      subtitle="How the 7 Generations Indigenous Trust collects, safeguards, and honors the sovereignty of your information."
    >
      <Clause n="1" t="Our Commitment to Data Sovereignty">
        <p>
          The 7 Generations Indigenous Trust ("the Trust," "we," "us") is founded on the principle of data
          sovereignty. Information you entrust to us — including lineage records, genealogical documents, and
          tribal registries — remains, to the fullest extent possible, under your control and is never sold,
          rented, or surrendered to external authorities for commercial purposes.
        </p>
      </Clause>
      <Clause n="2" t="Information We Collect">
        <p>We collect only what is necessary to serve verified constituents and sovereign nations:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong className="text-[#C9C4BD]">Account information</strong> — name, email, and profile details provided via Google sign-in (individual members) or the email/password you create (tribal accounts).</li>
          <li><strong className="text-[#C9C4BD]">Membership inquiry data</strong> — heritage, research status, enrollment status, location, phone, and messages you submit.</li>
          <li><strong className="text-[#C9C4BD]">Genealogical & governing records</strong> — documents, images, tribal rolls, and files you upload for anchoring.</li>
          <li><strong className="text-[#C9C4BD]">Identification media</strong> — any ID photo you choose to upload for your digital credential.</li>
          <li><strong className="text-[#C9C4BD]">Transaction data</strong> — payment status for physical ID cards and subscriptions (processed by Stripe; we do not store card numbers).</li>
        </ul>
      </Clause>
      <Clause n="3" t="How Your Information Is Used">
        <p>
          Your information is used solely to verify eligibility, issue credentials, anchor records to the
          Decentralized Blockchain Ancestry Registry, process orders and subscriptions, respond to inquiries,
          and operate the secure Vault and Tribal Records Repository.
        </p>
      </Clause>
      <Clause n="4" t="Storage, Security & Blockchain Anchoring">
        <p>
          Uploaded files are stored in encrypted object storage. Records are cryptographically hashed
          (SHA-256) and anchored to a decentralized registry, creating a permanent, tamper-evident reference.
          Anchored hashes are, by design, immutable and cannot be retroactively altered.
        </p>
      </Clause>
      <Clause n="5" t="Third-Party Services">
        <p>
          We rely on a limited set of trusted processors: Google (authentication), Stripe (payments), and
          secure object-storage infrastructure. Each processes data only as needed to deliver its function.
        </p>
      </Clause>
      <Clause n="6" t="Your Rights">
        <p>
          You may access, correct, or request deletion of your personal information at any time by contacting
          us. Deletion of an account removes associated personal data, though previously anchored cryptographic
          hashes remain immutable on the registry.
        </p>
      </Clause>
      <Clause n="7" t="Confidentiality">
        <p>
          Communications and records exchanged with the Trust are treated as confidential and are additionally
          governed by any Non-Disclosure Agreement you accept upon inquiry or nation registration.
        </p>
      </Clause>
      <Clause n="8" t="Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be reflected by an updated
          effective date on this page.
        </p>
      </Clause>
    </LegalShell>
  );
}

export function TermsOfService() {
  return (
    <LegalShell
      eyebrow="Legal · Terms"
      title="Terms of Service"
      subtitle="The terms governing your use of the 7 Generations Indigenous Trust portal, Vault, and Tribal Records Repository."
    >
      <Clause n="1" t="Acceptance of Terms">
        <p>
          By accessing or using this portal, you agree to these Terms of Service. If you do not agree, please
          discontinue use of the site and its services.
        </p>
      </Clause>
      <Clause n="2" t="Eligibility & Membership">
        <p>
          Membership is intended for independent descendants of Woodland Mississippian and Modern Freedmen
          heritage. Tribal Records Repository accounts are intended for sovereign nations and require an
          official .gov or .org email address. You are responsible for the accuracy of information you submit.
        </p>
      </Clause>
      <Clause n="3" t="Accounts & Security">
        <p>
          You are responsible for maintaining the confidentiality of your credentials and for all activity
          under your account. Notify us promptly of any unauthorized use.
        </p>
      </Clause>
      <Clause n="4" t="Records, Uploads & Conduct">
        <p>
          You represent that records and documents you upload are lawfully yours to submit and do not infringe
          the rights of others. You agree not to upload unlawful, fraudulent, or malicious content, or to
          attempt to disrupt or compromise the integrity of the registry or Vault.
        </p>
      </Clause>
      <Clause n="5" t="Payments & Subscriptions">
        <p>
          Physical ID cards are one-time purchases. Tribal Records Repository access is offered by paid
          subscription (monthly or annual) processed through Stripe. Subscriptions renew until cancelled;
          repository tools require an active subscription. Indefinite/perpetual arrangements are handled
          directly by contacting us. Fees are non-refundable except where required by law.
        </p>
      </Clause>
      <Clause n="6" t="Intellectual Property">
        <p>
          The Trust's names, marks, seals, emblems, and site content are the property of the Trust. Your
          records and documents remain yours; you grant us a limited license to store, process, and anchor
          them solely to provide the services.
        </p>
      </Clause>
      <Clause n="7" t="Nature of Credentials">
        <p>
          Credentials, digital IDs, and physical cards issued by the Trust represent membership within a
          private institutional framework. They are not government-issued identification and do not confer
          federal or state tribal recognition.
        </p>
      </Clause>
      <Clause n="8" t="Disclaimers & Limitation of Liability">
        <p>
          Services are provided "as is" without warranties of any kind. To the maximum extent permitted by
          law, the Trust shall not be liable for indirect, incidental, or consequential damages arising from
          use of the services.
        </p>
      </Clause>
      <Clause n="9" t="Termination">
        <p>
          We may suspend or terminate access for violation of these Terms. Provisions relating to
          intellectual property, disclaimers, and limitations of liability survive termination.
        </p>
      </Clause>
      <Clause n="10" t="Changes to These Terms">
        <p>
          We may revise these Terms from time to time. Continued use after changes constitutes acceptance of
          the revised Terms.
        </p>
      </Clause>
    </LegalShell>
  );
}

export function AccessibilityStatement() {
  return (
    <LegalShell
      eyebrow="Legal · Accessibility"
      title="Accessibility Statement"
      subtitle="Our commitment to an inclusive experience for every descendant and nation we serve."
    >
      <Clause n="1" t="Our Commitment">
        <p>
          The 7 Generations Indigenous Trust is committed to ensuring digital accessibility for people of all
          abilities. We continually work to improve the experience for everyone and to apply the relevant
          accessibility standards.
        </p>
      </Clause>
      <Clause n="2" t="Conformance Status">
        <p>
          We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1, Level AA. These guidelines
          explain how to make web content more accessible to people with a wide range of disabilities.
        </p>
      </Clause>
      <Clause n="3" t="Measures We Take">
        <ul className="list-disc pl-5 space-y-2">
          <li>Semantic structure, descriptive headings, and meaningful text alternatives for imagery.</li>
          <li>Sufficient color contrast between text and the deep-black/gold palette.</li>
          <li>Keyboard-navigable menus, forms, and interactive controls.</li>
          <li>Clear labels and test identifiers on interactive elements.</li>
          <li>Responsive layouts that adapt to zoom and a range of devices.</li>
        </ul>
      </Clause>
      <Clause n="4" t="Known Limitations">
        <p>
          Despite our efforts, some content — such as certain generated imagery or third-party embedded
          components — may not yet be fully accessible. We are actively working to address these areas.
        </p>
      </Clause>
      <Clause n="5" t="Feedback">
        <p>
          We welcome your feedback on the accessibility of this portal. If you encounter a barrier or need
          content provided in an alternative format, please contact us and we will make reasonable efforts to
          assist and improve.
        </p>
      </Clause>
    </LegalShell>
  );
}
